import { forwardRef, useMemo, useState } from 'react';
import { Group, Plane, Vector2, Vector2Tuple, Vector3 } from 'three';

import { useCursor } from '@react-three/drei';
import { GroupProps, MeshProps, ThreeEvent, useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { Polygon } from '../Polygon/index.js';

export interface PolygonControlHandleProps extends MeshProps {
  onDrag?: (payload: { position: Vector2; first: boolean; last: boolean }) => void;
  onRemove?: () => void;
  color?: string;
  size?: number;
  hoverCursor?: string;
}

export const PolygonControlHandle: React.FC<PolygonControlHandleProps> = ({
  onClick,
  onDrag,
  onRemove,
  color = 'white',
  hoverCursor = 'move',
  size = 0.1,
  ...restProps
}) => {
  const [hover, setHover] = useState(false);
  const { controls, camera, pointer, raycaster } = useThree();

  const bind = useDrag(({ event, down, first, last }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);

    onDrag?.({ position: new Vector2(pos.x, pos.z), first: first, last: last });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });

  useCursor(hover, hoverCursor);

  const handlePointerOver = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(true);
  });

  const handlePointerOut = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(false);
  });

  const handleClick = useEventCallback((evt: ThreeEvent<MouseEvent>) => {
    evt.stopPropagation();
    onClick?.(evt);
  });

  return (
    <mesh
      // react-gesture is not infering the EventTarget due always returns ReactDOMAttributes
      {...(bind() as any)}
      {...restProps}
      onClick={handleClick}
      onDoubleClick={onRemove}
      onPointerOut={handlePointerOut}
      onPointerOver={handlePointerOver}
    >
      <sphereGeometry args={[size, 16, 16]} />
      <meshBasicMaterial color={color} depthTest={false} />
    </mesh>
  );
};

export interface PolygonControlsProps extends GroupProps, Pick<MeshProps, 'onClick'> {
  points: Vector2Tuple[];
  onChange?: (evt: {
    type: 'remove' | 'add' | 'update';
    first: boolean;
    last: boolean;
    points: Vector2Tuple[];
  }) => void;
  disabled?: boolean;
}

export const PolygonControls: React.FC<PolygonControlsProps> = forwardRef<
  Group,
  PolygonControlsProps
>(({ points, onChange, onClick, disabled }, fowardedRef) => {
  const pointHandles = useMemo(
    () =>
      points.map((point, idx) => (
        <PolygonControlHandle
          key={`${idx}`}
          position={[point[0], 0, point[1]]}
          onClick={onClick}
          onDrag={({ position, last, first }) => {
            const newPoints = [...points];
            newPoints[idx] = [position.x, position.y] as Vector2Tuple;
            onChange?.({ type: 'update', last, first, points: newPoints });
          }}
          onRemove={() => {
            const newPoints = [...points.slice(0, idx), ...points.slice(idx + 1)];
            onChange?.({ type: 'remove', last: true, first: true, points: newPoints });
          }}
        />
      )),
    [onChange, onClick, points]
  );

  const closedLoop = useMemo(() => [...points, points[0]], [points]);

  // add create handles beetwen points
  const createHandles = useMemo(() => {
    const handles = [];
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[i + 1] ?? points[0];
      const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      handles.push(
        <PolygonControlHandle
          key={`create-${i}`}
          hoverCursor="crosshair"
          position={[mid[0], 0, mid[1]]}
          size={0.05}
          onClick={onClick}
          onDrag={({ position, first, last }) => {
            if (first) {
              const newPoints = [
                ...points.slice(0, i + 1),
                [position.x, position.y] as Vector2Tuple,
                ...points.slice(i + 1),
              ];

              onChange?.({ type: 'add', last, first, points: newPoints });
            } else {
              const newPoints = [...points];
              newPoints[i + 1] = [position.x, position.y] as Vector2Tuple;
              onChange?.({ type: 'update', last, first, points: newPoints });
            }
          }}
        />
      );
    }
    return handles;
  }, [onChange, onClick, points]);

  return (
    <group ref={fowardedRef}>
      <Polygon color="orange" lineWidth={2} points={closedLoop} />
      {!disabled && (
        <group renderOrder={1000}>
          {pointHandles}
          {createHandles}
        </group>
      )}
    </group>
  );
});
